import { RoundedButton } from '@/components/rounded-button';
import { AccordionItem, AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion';
import { getVaultFlowchartImageByKeyAndStep, NucleusVaultMetadata } from '@/constants/nucleus-vaults';
import { formatListWithOr } from '@/utils';
import { Box, Heading, Icon, Image, VStack } from '@chakra-ui/react';
import { getVaultByKey } from '@molecularlabs/nucleus-frontend';
import { useQuery } from '@tanstack/react-query';
import { RefObject, useState } from 'react';
import { HiMiniArrowLeft, HiMiniArrowRight } from 'react-icons/hi2';
import { useChainId } from 'wagmi';

export const HOW_IT_WORKS_ACCORDION_VALUE = 'how-does-the-vault-work';
export const HowDoesTheVaultWorkAccordion = ({
  vaultMetadata,
  value,
  setValue,
  ref,
}: {
  vaultMetadata: NucleusVaultMetadata;
  value: string[] | undefined;
  setValue: (v: string[]) => void;
  ref: RefObject<HTMLDivElement | null>;
}) => {
  const { vaultKey, name, marketId } = vaultMetadata;
  const chainId = useChainId();

  const { data: vault } = useQuery({
    queryKey: ['vault', vaultKey],
    queryFn: () => getVaultByKey(vaultKey),
  });

  const depositTokens = Object.values(vault?.deposit.sourceChains[chainId].depositTokens ?? {});
  const withdrawTokens = Object.values(
    vault?.withdraw?.sourceChains[chainId]?.destinationChains[chainId]?.wantTokens ?? {},
  );
  const [step, setStep] = useState(1);
  const maxSteps = marketId ? 3 : 2;
  const decrementStep = () => {
    if (step === 1) return;
    setStep(step - 1);
  };
  const incrementStep = () => {
    if (step === maxSteps) return;
    setStep(step + 1);
  };
  const getStepDescription = () => {
    if (step === 1) {
      return `Deposit ${formatListWithOr(depositTokens.map(({ symbol }) => symbol))} into the vault and receive ${vaultKey}`;
    }
    if (step === 2) {
      return `Deposit ${vaultKey} into Royco to earn $PLUME incentives on top of the base vault yield `;
    }
    return `Withdraw ${vaultKey} from Royco and exchange it for ${formatListWithOr(withdrawTokens.map(({ symbol }) => symbol))} by withdrawing from Optimizer Vault`;
  };
  return (
    <AccordionRoot mb={16} collapsible onValueChange={(e) => setValue(e.value)} value={value} ref={ref}>
      <AccordionItem
        key="how-does-the-vault-work"
        value={HOW_IT_WORKS_ACCORDION_VALUE}
        borderColor="border"
        borderWidth="thin"
        borderRadius="2xl"
        bgColor="bg"
      >
        <AccordionItemTrigger px={8} py={4} cursor="pointer">
          <VStack alignItems="start">
            <Heading color="fg" fontWeight="500" size="xl">
              How does the {name} work?
            </Heading>
            <Box>
              <Heading as="span" size="md" fontWeight="500" mr={2}>
                Step {step}
              </Heading>
              <Heading as="span" size="md" fontWeight="500" color="fg.muted">
                {getStepDescription()}
              </Heading>
            </Box>
          </VStack>
        </AccordionItemTrigger>
        <AccordionItemContent p={6} pt={0}>
          <RoundedButton onClick={decrementStep} variant="outline" bgColor="bg" h={6} disabled={step <= 1} mr={4}>
            <Icon boxSize={4}>
              <HiMiniArrowLeft />
            </Icon>
          </RoundedButton>

          <RoundedButton onClick={incrementStep} variant="outline" bgColor="bg" h={6} disabled={step >= maxSteps}>
            <Icon boxSize={4}>
              <HiMiniArrowRight />
            </Icon>
          </RoundedButton>

          <Image src={getVaultFlowchartImageByKeyAndStep(vaultKey, step)} mt={4} />
        </AccordionItemContent>
      </AccordionItem>
    </AccordionRoot>
  );
};
